import { Component, Prop, Ref, Watch } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import { disableBodyScrolling } from '@/utils/disableBodyScrolling';
import { formatMoney } from '@/utils/formatting';
import { cart } from '@/theme/Cart';
import { ProductOptionPicker } from '@/components/Product/ProductOptionPicker';
import { ProductForm } from '@/components/Product/ProductForm';

@Component
export class FramesetOverlay extends ThemeComponent {
    @Prop product!: Shopify.Liquid.Product;
    @Prop selectedVariant!: Shopify.Liquid.Variant;
    @Prop({default: ''}) selectedProperty!: string;
    @Prop selectedVariantPrice!: number;
    @Prop selectedVariantSalePrice!: number;
    @Prop additionalProductsPrice!: number;
    @Prop subtotal!: number;
    @Prop saleSubtotal!: number;
    @Prop({default: window.scrollY}) scrollPosition!: number;

    @Ref() closeFramesetOverlayButton!: HTMLElement[];
    @Ref() additionalProduct!: HTMLElement[];
    @Ref() framesetSubtotal!: HTMLElement;
    @Ref() framesetSaleSubtotal!: HTMLElement;
    @Ref() framesetSubmit!: HTMLElement;
    @Ref() framesetError!: HTMLElement;
    @Ref() framesetErrorMessage!: HTMLElement;
    @Ref() framesetErrorClose!: HTMLElement;
    @Ref() framesetSelectedVariantOption!: HTMLElement[];
    @Ref({required: false}) framesetSelectedPropertyOption!: HTMLElement;

    init() {

        // Give initial value for scrollPosition
        this.scrollPosition = window.scrollY;

        // Add visibility click event listeners
        this.addVisibilityEventListeners();

        // Handle additional product selection
        this.handleAdditionalProductSelection();

        // Listen for product form variant changes
        this.listenForProductFormVariantChange();

        // Listen for product form property changes
        if (this.framesetSelectedPropertyOption) {
            this.listenForProductFormPropertyChange();
        }

        // Listen for submit
        this.onSubmit();

        // Listen for close error event
        this.listenForCloseErrorEvent();

        // Listen for Escape key to close overlay
        this.listenForEscapeKey();
    }

    @Watch('selectedVariant')
    protected handleSelectedVariantChanged() {
        this.selectedVariantPrice = this.selectedVariant.price;
        if (this.selectedVariant.compare_at_price != null) {
            this.selectedVariantSalePrice = this.selectedVariant.compare_at_price;
        }

        // Update selected variant options
        for (const option of this.framesetSelectedVariantOption) {
            const optionIdAttribute = option.getAttribute('data-option-id');
            const variantKey = `option${optionIdAttribute}` as ShopifyProductOptionKey;
            option.textContent = this.selectedVariant[variantKey];
        }
    }

    @Watch('selectedVariantPrice')
    protected handleSelectedVariantPriceChanged() {
        this.subtotal = this.selectedVariantPrice + this.additionalProductsPrice;
    }

    @Watch('selectedVariantSalePrice')
    protected handleSelectedVariantSalePriceChanged() {
        this.saleSubtotal = this.selectedVariantSalePrice + this.additionalProductsPrice;
    }

    @Watch('additionalProductsPrice')
    protected handleAdditionalProductsPriceChanged() {
        this.subtotal = this.selectedVariantPrice + this.additionalProductsPrice;
        this.saleSubtotal = this.selectedVariantSalePrice + this.additionalProductsPrice;
    }

    @Watch('subtotal')
    protected handleSubtotalChanged() {
        this.framesetSubtotal.innerHTML = formatMoney(this.subtotal);
    }

    @Watch('saleSubtotal')
    protected handleSaleSubtotalChanged() {
        this.framesetSaleSubtotal.innerHTML = formatMoney(this.saleSubtotal);
    }

    addVisibilityEventListeners() {
        const framesetTriggerButton = document.querySelector('[data-ref="framesetTriggerButton"]');
        if (framesetTriggerButton) {
            const framesetTriggerButtonElem = framesetTriggerButton as HTMLElement;
            this.$listen(framesetTriggerButtonElem, 'click', () => {
                this.classList.add('frameset-overlay--active');
                document.body.classList.add('page-scrolled-up');
                document.body.classList.remove('page-scrolled-down');

                // Disable body scrolling
                this.scrollPosition = disableBodyScrolling(true, this.scrollPosition);
            });
        }
        for (const button of this.closeFramesetOverlayButton) {
            this.$listen(button, 'click', () => {
                this.classList.remove('frameset-overlay--active');

                // Enable body scrolling
                this.scrollPosition = disableBodyScrolling(false, this.scrollPosition);
            });
        }
    }

    handleAdditionalProductSelection() {
        for (const additionalProduct of this.additionalProduct) {
            // Add tabindex to make items focusable
            additionalProduct.setAttribute('tabindex', '0');
            additionalProduct.setAttribute('role', 'button');
            additionalProduct.setAttribute('aria-pressed', 'false');

            this.$listen(additionalProduct, 'click', () => {
                this.toggleAdditionalProduct(additionalProduct);
            });

            // Add keyboard event listener
            this.$listen(additionalProduct, 'keydown', (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    this.toggleAdditionalProduct(additionalProduct);
                }
            });
        }
    }

    private toggleAdditionalProduct(additionalProduct: HTMLElement) {
        // Determine if we are selecting or unselecting the item
        let isSelected = true;
        if (additionalProduct.classList.contains('frameset-overlay-additional-products-item--selected')) {
            isSelected = false;
        }

        // Toggle selected class
        additionalProduct.classList.toggle('frameset-overlay-additional-products-item--selected');

        // Update aria-pressed attribute
        const isNowSelected = additionalProduct.classList.contains(
            'frameset-overlay-additional-products-item--selected'
        );
        additionalProduct.setAttribute('aria-pressed', isNowSelected.toString());

        // Get variant price
        const variantPriceAttribute = additionalProduct.getAttribute('data-variant-price');
        if (variantPriceAttribute) {
            const variantPrice = parseInt(variantPriceAttribute, 10);
            this.updateAdditionalProductsPrice(variantPrice, isSelected);
        }
    }

    updateAdditionalProductsPrice(price: number, toAdd: boolean) {
        if (toAdd) {
            this.additionalProductsPrice += price;
        } else {
            this.additionalProductsPrice -= price;
        }
    }

    listenForProductFormVariantChange() {
        const productOptionPicker = document.getElementById('product-form-option-picker');
        if (productOptionPicker && productOptionPicker.tagName === 'PRODUCT-OPTION-PICKER') {
            const productOptionPickerComponent = productOptionPicker as ProductOptionPicker;
            // eslint-disable-next-line max-len
            this.$subscribe(productOptionPickerComponent.onSelectedVariantChange, (variant: Shopify.Liquid.Variant|null) => {
                if (variant) {
                    this.selectedVariant = variant;
                }
            });
        }
    }

    listenForProductFormPropertyChange() {
        const productForm = document.querySelector('.product-form');
        if (productForm && productForm.tagName === 'PRODUCT-FORM') {
            const productFormComponent = productForm as ProductForm;
            // eslint-disable-next-line max-len
            this.$subscribe(productFormComponent.onSelectedPropertyChange, (value: string) => {
                this.framesetSelectedPropertyOption.textContent = value;
            });
        }
    }

    onSubmit() {
        this.$listen(this.framesetSubmit, 'click', async() => {

            try {

                // Check variant exists
                const variant = this.selectedVariant;
                if (!variant) {
                    return;
                }

                // Define array of cart promises
                const addToCartPromises = [];

                // Define line item properties shape
                const properties: {[key: string]: string} = {};

                // Force Zapiet properties
                // properties['_ZapietId'] = 'M=S';

                // Check if line item properties are needed - currently only using HEADSET_AND_SEAT_CLAMP_COLOUR
                const additionalOptionRef = document.querySelector('[data-ref="additionalOptionInput"]');
                if (additionalOptionRef) {
                    const additionalOptionInput = additionalOptionRef as HTMLInputElement;
                    properties[additionalOptionInput.name] = additionalOptionInput.value.toUpperCase();
                }

                // Add product variant to cart promises
                addToCartPromises.push({
                    id: variant.id,
                    quantity: 1,
                    properties: properties
                });

                // Define additional line item properties shape
                const additionalProperties: {[key: string]: string} = {};

                // Force Zapiet properties
                // additionalProperties['_ZapietId'] = 'M=S';

                // Add compare price to line item properties
                if (Number(this.framesetSubmit.dataset.comparePrice) > this.product.price) {
                    properties['_compare'] = String(this.framesetSubmit.dataset.comparePrice);
                }

                // Add additional products to cart promises (if selected)
                for (const additionalProduct of this.additionalProduct) {
                    if (additionalProduct.classList.contains('frameset-overlay-additional-products-item--selected')) {
                        const additionalProductVariantId = Number(additionalProduct.getAttribute('data-variant-id'));
                        addToCartPromises.push({
                            id: additionalProductVariantId,
                            quantity: 1,
                            properties: additionalProperties
                        });
                    }
                }

                // Add all items
                await cart.addItems(addToCartPromises);

                // Close overlay on success
                this.classList.remove('frameset-overlay--active');

                // Enable body scrolling
                this.scrollPosition = disableBodyScrolling(false, this.scrollPosition);

                // Dispatch event for sidecart
                document.dispatchEvent(new Event('cartOpen'));
            } catch (event) {
                this.$log.error('Cart error', {
                    event
                });
                const { message } = event as Error;
                this.showError(message);
            }
        });
    }

    protected showError(msg: string|null): void {
        this.framesetErrorMessage.textContent = msg ? msg.split('.')[0] : '';
        if (msg) {
            this.framesetError.classList.add('frameset-overlay-error--active');
        } else {
            this.framesetError.classList.remove('frameset-overlay-error--active');
        }
    }

    listenForCloseErrorEvent() {
        this.$listen(this.framesetErrorClose, 'click', () => {
            this.framesetError.classList.remove('frameset-overlay-error--active');
        });
    }

    listenForEscapeKey() {
        this.$listen(window, 'keydown', (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                if (this.classList.contains('frameset-overlay--active')) {
                    this.classList.remove('frameset-overlay--active');

                    // Enable body scrolling
                    this.scrollPosition = disableBodyScrolling(false, this.scrollPosition);
                }
            }
        });
    }
}
